import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CreditCard, ExternalLink, ShieldCheck, X } from "lucide-react";
import { useCheckout } from "../contexts/CheckoutContext";
import { useWallet } from "../hooks/useWallet";
import { formatPrice, listPackages, listPlans } from "../lib/billing";
import GhostButton from "./GhostButton";
import GoldButton from "./GoldButton";

/**
 * CheckoutModal — Stripe Checkout hosted-redirect flow (Phase 9).
 *
 * Behavior:
 *  - Fetches server-side catalog (never trusts client-side prices).
 *  - Shows a compact confirmation card with amount + "You'll be redirected to
 *    Stripe" notice + supported-cards badges.
 *  - On confirm: POST /api/billing/checkout with { target, origin_url } and
 *    window.location.href = data.url. Post-redirect confirmation lives in
 *    pages/Billing.jsx via ?success=true&session_id=...
 *  - No card form here anymore — Stripe hosts the form.
 */
export function CheckoutModal() {
  const { t } = useTranslation();
  const { open, kind, packageId, planId, closeCheckout } = useCheckout();
  const { purchaseTokens, subscribePro } = useWallet();

  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let live = true;
    (async () => {
      const [pkgRes, planRes] = await Promise.all([listPackages(), listPlans()]);
      if (!live) return;
      if (pkgRes.ok) setPackages(pkgRes.data);
      if (planRes.ok) setPlans(planRes.data);
    })();
    return () => {
      live = false;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setError("");
      setBusy(false);
    }
  }, [open, kind, packageId, planId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeCheckout();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeCheckout]);

  const selectedPackage = useMemo(
    () => packages.find((p) => p.id === packageId) || null,
    [packages, packageId],
  );
  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === planId) || null,
    [plans, planId],
  );

  const isDowngrade = kind === "subscription" && planId === "free";
  const isUpgrade = kind === "subscription" && planId === "pro";
  const isRecharge = kind === "recharge";

  const amountCents = isRecharge
    ? selectedPackage?.price_cents ?? 0
    : isUpgrade
      ? selectedPlan?.price_cents ?? 0
      : 0;
  const amountText = formatPrice(amountCents);

  const title = isUpgrade
    ? t("checkout.titleUpgrade")
    : isRecharge
      ? t("checkout.titleRecharge") +
        (selectedPackage ? ` — ${selectedPackage.nxt} NXT` : "")
      : isDowngrade
        ? t("checkout.titleDowngrade")
        : t("checkout.title");

  const onContinue = async () => {
    if (busy) return;
    setError("");

    if (isDowngrade) {
      // MVP: recurring not wired, downgrade is a no-op with a friendly toast.
      toast.info(t("checkout.downgradeUnavailable"));
      closeCheckout();
      return;
    }

    setBusy(true);
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const res = isRecharge
      ? await purchaseTokens(packageId, origin)
      : await subscribePro(origin);
    setBusy(false);

    if (!res.ok || !res.data?.url) {
      setError(t("checkout.errorGeneric"));
      return;
    }
    // Full-page redirect to Stripe-hosted checkout
    window.location.href = res.data.url;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="checkout-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md p-4"
          onClick={closeCheckout}
          data-testid="checkout-backdrop"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#08090f]/95 p-7"
            onClick={(e) => e.stopPropagation()}
            data-testid="checkout-modal"
          >
            <button
              type="button"
              onClick={closeCheckout}
              aria-label={t("common.close")}
              className="absolute right-4 top-4 text-white/50 hover:text-white transition-colors"
              data-testid="checkout-close-btn"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#D4AF37]/80">
              <CreditCard className="h-3.5 w-3.5" />
              {t("checkout.title")}
            </div>
            <h2
              className="mt-2 font-display text-2xl sm:text-3xl text-white tracking-tight"
              data-testid="checkout-title"
            >
              {title}
            </h2>

            {isDowngrade ? (
              <>
                <p className="mt-4 text-sm text-white/70">
                  {t("checkout.downgradeUnavailable")}
                </p>
                <div className="mt-6 flex flex-wrap justify-end gap-2">
                  <GhostButton onClick={closeCheckout}>{t("checkout.cancel")}</GhostButton>
                </div>
              </>
            ) : (
              <>
                {/* Amount */}
                <div className="mt-5 flex items-baseline justify-between rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3">
                  <span className="text-sm text-white/55">{t("checkout.amountDue")}</span>
                  <span
                    className="font-display text-2xl text-white tabular-nums"
                    data-testid="checkout-amount"
                  >
                    {amountText}
                  </span>
                </div>

                {/* Stripe redirect notice */}
                <div className="mt-5 flex gap-3 rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300/90" />
                  <div className="text-xs leading-relaxed text-white/70">
                    <div className="font-medium text-white/85">
                      {t("checkout.stripeRedirectTitle")}
                    </div>
                    <div className="mt-1">{t("checkout.stripeRedirectBody")}</div>
                  </div>
                </div>

                {/* Card brands */}
                <div
                  className="mt-4 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/45"
                  data-testid="checkout-card-brands"
                >
                  <span>Visa</span>
                  <span className="opacity-30">·</span>
                  <span>Mastercard</span>
                  <span className="opacity-30">·</span>
                  <span>Amex</span>
                  <span className="opacity-30">·</span>
                  <span>Discover</span>
                </div>

                {error && (
                  <div
                    className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200"
                    data-testid="checkout-error"
                  >
                    {error}
                  </div>
                )}

                <div className="mt-6 flex items-center justify-end gap-2">
                  <GhostButton type="button" onClick={closeCheckout} disabled={busy}>
                    {t("checkout.cancel")}
                  </GhostButton>
                  <GoldButton
                    type="button"
                    onClick={onContinue}
                    disabled={busy}
                    data-testid="checkout-submit-btn"
                    className="gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {busy ? t("checkout.processing") : t("checkout.continueSecure")}
                  </GoldButton>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CheckoutModal;
